"use client";
import React, { useCallback, useEffect, useState } from "react";
import "./styles.css";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  Dropdown,
  Field,
  Link,
  Spinner,
  Toast,
  Toaster,
  ToastTitle,
  ToastTrigger,
  useId,
  useToastController,
} from "@fluentui/react-components";
import ClientSelection from "./Clients";
import apiServices from "../../../service";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import TemplateSelectionModal, { InputField } from "./TemplateSelectionModal";
import CaseSelectionModal from "./CaseSelectionModal";
import OverlayLoader from "../../../components/OverlayLoader";
import store from "../../../redux/store";
import axios from "axios";
import { useDispatch } from "react-redux";
import ASCButton from "../../../components/Button";
import TextEditor from "../../../components/TextEditor";
import { stripHtml } from "../../../utils";

type selectedValue = {
  customer_id: string;
  first_names: string;
  last_name: string;
};

const MAX_NOTE_LENGTH = 5000;

const LetterGeneration = () => {
  const [showClientSelection, setShowClientSelection] = useState(false);
  const [clients, setClients] = useState<selectedValue[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedClient, setSelectedClient] = useState<selectedValue[]>([]);
  const [templateListing, setTemplateListing] = useState<any>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>("");
  const [openSampleTemplate, setOpenSampleTemplate] = useState<boolean>(false);
  const [selectedTemplateCode, setSelectedTemplateCode] = useState<any>("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [sampleTemplate, setSampleTemplate] = useState<any>([]);
  const [createContactHistory, setCreateContactHistory] = useState(false);
  const [contactHistoryNote, setContactHistoryNote] = useState("");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);
  const [hasCasePlaceholder, setHasCasePlaceholder] = useState(false);
  const [selectedCaseIds, setSelectedCaseIds] = useState<string[]>([]);
  const [selectedCaseDisplay, setSelectedCaseDisplay] = useState("");
  const [showCaseSelectionModal, setShowCaseSelectionModal] = useState(false);
  const [pendingClientIds, setPendingClientIds] = useState<string[]>([]);
  // 1. New tracked state to monitor if the cursor has interacted with the text area
  const [isNoteTouched, setIsNoteTouched] = useState<boolean>(false);

  const dispatch = useDispatch();
  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);

  const notify = (message?: any, type?: any) =>
    dispatchToast(
      <Toast>
        <ToastTitle
          action={
            <ToastTrigger>
              <Link>Dismiss</Link>
            </ToastTrigger>
          }
        >
          {message}
        </ToastTitle>
      </Toast>,
      { intent: type ? type : "success" }
    );

  const resetAll = () => {
    setSelectedClient([]);
    setSelectedTemplateCode([]);
    setSelectedTemplate("");
    setSelectedOptions([]);
    setCreateContactHistory(false);
    setContactHistoryNote("");
    setIsNoteTouched(false);
    setHasCasePlaceholder(false);
    setSelectedCaseIds([]);
    setSelectedCaseDisplay("");
  };

  const handleContactHistoryToggle = useCallback(
    (_: any, data: { checked: boolean | "mixed" }) => {
      const checked = data.checked === true;
      setCreateContactHistory(checked);
      if (!checked) {
        setContactHistoryNote("");
        setIsNoteTouched(false); // Clear status if unticked
      }
    },
    []
  );

  const handleNoteChange = useCallback((value: string) => {
    setContactHistoryNote(value);
  }, []);

  // 2. Callback executed when user pulls focus (clicks away) out of the input box
  const handleNoteBlur = useCallback(() => {
    setIsNoteTouched(true);
  }, []);

  const noteCharCount = stripHtml(contactHistoryNote).trim().length;

  // 3. Updated check condition: Only marks empty as error if explicitly touched/blurred out
  const noteError =
    createContactHistory && isNoteTouched && noteCharCount === 0
      ? "Contact history remarks are required"
      : noteCharCount > MAX_NOTE_LENGTH
        ? `Must not exceed ${MAX_NOTE_LENGTH} characters`
        : "";

  // 4. Form Submission Block Validation Logic Check
  const isGenerateDisabled =
    !(selectedOptions?.length > 0 && selectedTemplateCode?.length > 0) ||
    (createContactHistory && noteCharCount === 0) ||
    noteCharCount > MAX_NOTE_LENGTH ||
    (hasCasePlaceholder && selectedCaseIds.length === 0);

  const getLetterList = () => {
    setTemplatesLoading(true);
    apiServices.TemplateEngine.getLetterTemplate().then((res) => {
      const data = Array.isArray(res?.data) ? res.data : [];
      const filteredTemplates = data
        .filter((item: any) => item.status === "Completed" && item.code !== null)
        .map((item: any) => ({
          title: item.title,
          code: item.code,
          description: item.description,
          status: item.status,
          inputFields: item.inputFields || [],
        }));
      setTemplateListing(filteredTemplates);
    }).catch(() => {
      setTemplateListing([]);
      notify("Failed to load templates.", "error");
    }).finally(() => {
      setTemplatesLoading(false);
    });
  };

  useEffect(() => {
    getLetterList();
  }, []);

  // Sync pending selection from confirmed whenever the dialog opens
  useEffect(() => {
    if (showClientSelection) {
      setPendingClientIds(selectedOptions);
    }
  }, [showClientSelection]);

  const handleTemplateClick = (title: string, code: string, inputFields: InputField[]) => {
    setSelectedClient([]);
    setSelectedOptions([]);
    setSelectedCaseIds([]);
    setSelectedCaseDisplay("");
    setSelectedTemplate(title);
    setSelectedTemplateCode(code);
    setHasCasePlaceholder(inputFields.some((f) => f.Name === "Case"));
  };

  const handleClientSelection = (selectedIds: string[]) => {
    setPendingClientIds(selectedIds);
  };

  const commitClientSelection = () => {
    const confirmedClients = clients.filter((client: any) =>
      pendingClientIds.includes(client?.customer_id)
    );
    setSelectedOptions(confirmedClients.map((c) => c.customer_id));
    setSelectedClient(confirmedClients);
    setSelectedCaseIds([]);
    setSelectedCaseDisplay("");
    setShowClientSelection(false);
  };

  let timestamp: any = new Date();
  timestamp = timestamp.toISOString();
  const generateNonce = () => {
    const CryptoJS = require("crypto-js");
    const requestData = {};
    const hash = CryptoJS.SHA256(`${requestData}${timestamp}`);
    return hash.toString(CryptoJS.enc.Hex);
  };
  const nonce = generateNonce();

  const generateLetter = async () => {
    try {
      setLoading(true);
      const requestData: any = {
        customerIds: selectedOptions,
        reportCode: selectedTemplateCode,
        ...(hasCasePlaceholder && selectedCaseIds.length > 0 ? { caseIds: selectedCaseIds } : {}),
      };
      createContactHistory && (requestData["createNote"] = true);
      createContactHistory && (requestData["noteText"] = contactHistoryNote);

      const token = store.getState().authUser.userToken;
      const userId = store.getState().authUser.userId;
      const refreshToken = store.getState().authUser.refreshToken;

      const fileExtension = "zip";
      // const url = `${process.env.REACT_APP_BASE_URL}report/new/custom/${selectedTemplateCode}`;
      const url = `${process.env.REACT_APP_BASE_URL}orchestration/letters/generate`;

      const response = await axios.post(url, requestData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Nonce": nonce,
          "X-Timestamp": timestamp,
          UserId: userId,
          RefreshToken: refreshToken,
        },
        responseType: "blob",
      }).then((response) => {
        // const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
        // const link = document.createElement("a");
        // link.href = fileUrl;
        // link.setAttribute("download", `${selectedTemplateCode}.${fileExtension}`);

        // document.body.appendChild(link);
        // link.click();

        notify("Letter generation has been initiated successfully.", "success");

        // window.URL.revokeObjectURL(fileUrl);
        // document.body.removeChild(link);
      })
        .catch((error: any) => {
          notify(error.message, "error");
          console.error("Error generating the letter:", error);
        })
        .finally(() => {
          setLoading(false);
          resetAll();
        });

      // const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
      // const link = document.createElement("a");
      // link.href = fileUrl;
      // link.setAttribute("download", `${selectedTemplateCode}.${fileExtension}`);

      // document.body.appendChild(link);
      // link.click();
      // notify("Letter Generated Successfully", "success");

      // window.URL.revokeObjectURL(fileUrl);
      // document.body.removeChild(link);
    } catch (error: any) {
      notify(error.message, "error");
      console.error("Error generating the letter:", error);
    } finally {
      setLoading(false);
      resetAll();
    }
  };

  const columnsData: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "title",
      header: "Title",
    },
    {
      accessorKey: "description",
      header: "Description",
    },
    {
      accessorKey: "signedUrl",
      header: "Link",
      Cell: ({ row }: any) => (
        <>
          <a href={row.original.signedUrl} style={{ textDecoration: "none" }}>
            {row.original.title} - {row.original.templateFilePath}
          </a>{" "}
        </>
      ),
    },
  ];

  const handleRowSelection = (row: any) => {
    setSelectedRow(row.original);
  };

  const getClientPlaceholder = () => {
    if (selectedClient.length === 0) {
      if (hasCasePlaceholder) {
        return "Select a Client (Single Client Only)";
      }
      else
        return "Select a Client / Multiple Clients";
    } else if (selectedClient.length === 1) {
      return `${selectedClient[0].first_names} ${selectedClient[0].last_name}`;
    } else if (selectedClient.length === 2) {
      return `${selectedClient[0].first_names} ${selectedClient[0].last_name}, ${selectedClient[1].first_names} ${selectedClient[1].last_name}`;
    } else {
      return `${selectedClient[0].first_names}, ${selectedClient[1].first_names} + ${selectedClient.length - 2} others`;
    }
  };

  return (
    <div>
      <OverlayLoader isLoading={loading} />
      <Toaster
        toasterId={toasterId}
        position="top-end"
        pauseOnHover
        pauseOnWindowBlur
        timeout={1000}
      />
      <div className="main-container">
        <div style={{ maxWidth: 420, display: "flex", flexDirection: "column", gap: 16 }}>

          {/* ── Select Template ── */}
          <Field label="Select Template :">
            <div
              role="button"
              tabIndex={loading ? -1 : 0}
              onClick={() => !loading && setShowTemplateModal(true)}
              onKeyDown={(e) =>
                e.key === "Enter" && !loading && setShowTemplateModal(true)
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                minHeight: 36,
                padding: "0 10px",
                border: "1px solid #d1d9e6",
                borderRadius: 5,
                background: "#fff",
                cursor: loading ? "not-allowed" : "pointer",
                width: 420,
                opacity: loading ? 0.6 : 1,
                userSelect: "none",
                transition: "border-color 0.15s",
                boxSizing: "border-box",
              }}
            >
              <span
                style={{
                  flex: 1,
                  fontSize: 12.5,
                  color: selectedTemplate ? "#1a202c" : "#9aa5b8",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedTemplate || "Select a template"}
              </span>
              <span
                style={{
                  display: "inline-block",
                  width: 1,
                  height: 18,
                  background: "#d1d9e6",
                  margin: "0 2px",
                  flexShrink: 0,
                }}
              />
              {loading ? (
                <Spinner size="tiny" />
              ) : (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  style={{ color: "#9aa5b8", flexShrink: 0 }}
                >
                  <path
                    d="M2.5 4.5L6 8l3.5-3.5"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </Field>

          {/* ── Select Client(s) ── */}
          <Field label="Select Client(s) :">
            <Dropdown
              style={{ width: "420px" }}
              placeholder={getClientPlaceholder()}
              onClick={() => setShowClientSelection(true)}
              disabled={selectedTemplate.length === 0}
            />
          </Field>

          {/* ── Select Case (visible only when template has case placeholder and client selected) ── */}
          {hasCasePlaceholder && selectedOptions.length > 0 && (
            <Field label="Select Case :">
              <div
                role="button"
                tabIndex={0}
                onClick={() => setShowCaseSelectionModal(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowCaseSelectionModal(true);
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  minHeight: 36,
                  padding: "0 10px",
                  border: "1px solid #d1d9e6",
                  borderRadius: 5,
                  background: "#fff",
                  cursor: "pointer",
                  width: 420,
                  userSelect: "none",
                  boxSizing: "border-box",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    fontSize: 12.5,
                    color: selectedCaseDisplay ? "#1a202c" : "#9aa5b8",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedCaseDisplay || "— Select a case —"}
                </span>
              </div>
            </Field>
          )}

          {/* ── Contact History ── */}
          <div>
            <Checkbox
              checked={createContactHistory}
              onChange={handleContactHistoryToggle}
              label="Create contact history entry for letter?"
            />

            {createContactHistory && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 4 }}>
                  <span style={{ fontSize: "14px", fontWeight: 500, color: "#242424" }}>
                    Contact History Remarks <span style={{ color: "#dc2626" }}>*</span>
                  </span>
                  <span
                    style={{
                      fontSize: 11.5,
                      color: noteCharCount > MAX_NOTE_LENGTH ? "#dc2626" : "#9aa5b8",
                    }}
                  >
                    {noteCharCount} / {MAX_NOTE_LENGTH}
                  </span>
                </div>

                <Field
                  validationState={noteError ? "error" : "none"}
                  validationMessage={noteError}
                >
                  {/* Added an onBlur intercept wrapper to update interaction states safely */}
                  <div
                    onBlur={handleNoteBlur}
                    style={{
                      border: `1px solid ${noteError ? "#dc2626" : "#d1d9e6"}`,
                      borderRadius: 5,
                      overflow: "hidden",
                      background: "#fff",
                    }}
                  >
                    <TextEditor
                      inputData={contactHistoryNote}
                      onChange={handleNoteChange}
                    />
                  </div>
                </Field>
              </div>
            )}
          </div>

          {/* ── Action row ── */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 2 }}>
            <ASCButton
              shape="rounded"
              appearance="secondary"
              onClick={resetAll}
              label="Cancel"
              disabled={loading}
            />
            <ASCButton
              shape="rounded"
              appearance="primary"
              className="asc-button-primary"
              onClick={generateLetter}
              label="Generate"
              disabled={isGenerateDisabled || loading}
            />
          </div>

        </div>
      </div>

      {/* Client Selection Modal Dialog */}
      <Dialog
        modalType="alert"
        open={showClientSelection}
        onOpenChange={(event, data) => setShowClientSelection(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <span className="mui-table">Client Selection</span>
            <DialogContent>
              <ClientSelection
                selectionMode={hasCasePlaceholder ? "radio" : "checkbox"}
                handleClientSelection={handleClientSelection}
                setClientList={setClients}
                initialSelectedIds={selectedOptions}
              />
            </DialogContent>
            <DialogActions>
              <ASCButton
                shape="rounded"
                size="medium"
                appearance="secondary"
                onClick={() => setShowClientSelection(false)}
                label="Cancel"
              />
              <ASCButton
                shape="rounded"
                className="asc-button-primary"
                appearance="primary"
                onClick={commitClientSelection}
                size="medium"
                label="Select"
              />
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Template Selection Modal */}
      <TemplateSelectionModal
        open={showTemplateModal}
        templates={templateListing}
        isTemplatesLoading={templatesLoading}
        selectedTitle={selectedTemplate}
        selectedCode={
          Array.isArray(selectedTemplateCode) ? "" : selectedTemplateCode || ""
        }
        onSelect={(title, code, inputFields) => {
          handleTemplateClick(title, code, inputFields);
          setShowTemplateModal(false);
        }}
        onClose={() => setShowTemplateModal(false)}
      />

      {/* Case Selection Modal */}
      {showCaseSelectionModal && (
        <CaseSelectionModal
          open={showCaseSelectionModal}
          customerId={selectedOptions[0] ?? ""}
          onSelect={(caseId, displayText) => {
            setSelectedCaseIds([caseId]);
            setSelectedCaseDisplay(displayText);
            setShowCaseSelectionModal(false);
          }}
          onClose={() => setShowCaseSelectionModal(false)}
        />
      )}

      {/* Sample Templates Modal Dialog */}
      <Dialog modalType="alert" open={openSampleTemplate}>
        <DialogSurface>
          <DialogBody>
            <span style={{ fontWeight: 600, fontSize: "16px" }}>
              Sample Templates{" "}
            </span>
            <DialogContent>
              <MaterialReactTable
                columns={columnsData}
                data={sampleTemplate}
                manualPagination
                enableGlobalFilter={true}
                enableColumnActions={false}
                enableColumnFilters={true}
                enableRowSelection
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => handleRowSelection(row),
                })}
                muiTableBodyCellProps={() => ({
                  style: {
                    padding: "6px 12px",
                    fontSize: "0.875rem",
                  },
                })}
                enableMultiRowSelection={false}
                enableFullScreenToggle={false}
                muiTablePaginationProps={{
                  showFirstButton: false,
                  showLastButton: false,
                }}
                onGlobalFilterChange={(text: string) => {
                  if (text) {
                    setSearchText(text);
                  } else {
                    setSearchText("");
                  }
                }}
              />
            </DialogContent>
            <DialogActions>
              <ASCButton
                shape="rounded"
                size="medium"
                onClick={() => {
                  setOpenSampleTemplate(false);
                  setSelectedRow(null);
                }}
                label="Close"
              />
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default LetterGeneration;